import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Plus, Search, ChevronLeft, ChevronRight, 
  RefreshCw, Lock, Users, ArrowRight 
} from 'lucide-react';
import toast from 'react-hot-toast';

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get('/api/roles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRoles(res.data);
    } catch (err) {
      toast.error("Failed to load security roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  // فلترة محلية سريعة للرولز (بما أن عددهم غالباً بيبقى قليل)
  const filteredRoles = roles.filter(role => 
    role.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6] text-[11px] font-bold uppercase tracking-tighter">
      
      {/* Toolbar */}
      <div className="bg-white p-3 border-b shadow-sm flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center gap-6 ml-4">
          <h2 className="text-[#005495] text-lg italic font-black flex items-center gap-2">
            <Lock size={20} className="text-orange-500" /> SECURITY ROLES
          </h2>
          
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={14} />
            <input 
              className="pl-9 pr-4 py-2 border border-gray-300 w-80 outline-none focus:border-blue-600 shadow-inner rounded-sm" 
              placeholder="Filter Roles..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <button 
          onClick={() => navigate('/roles/new')} 
          className="bg-[#005495] text-white px-8 py-2.5 rounded-sm flex items-center gap-2 hover:bg-black transition-all mr-4 shadow-lg active:scale-95"
        >
          <Plus size={18} /> CREATE NEW ROLE
        </button>
      </div>

      {/* Grid Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 text-blue-900 animate-pulse">
            <RefreshCw size={40} className="animate-spin mb-4" />
            <span className="tracking-[0.2em]">Loading Security Matrix...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRoles.map((role) => (
              <div 
                key={role.role_id}
                onClick={() => navigate(`/roles/${role.role_id}`)}
                className="bg-white border-2 border-gray-200 rounded-sm p-5 hover:border-blue-500 hover:shadow-xl cursor-pointer transition-all group relative overflow-hidden"
              >
                {/* Background Decoration */}
                <Shield size={80} className="absolute -right-5 -bottom-5 text-gray-50 group-hover:text-blue-50 transition-colors" />
                
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-2 bg-blue-50 rounded-sm text-blue-900">
                      <Shield size={24} />
                    </div>
                    <span className="text-[9px] bg-gray-100 px-2 py-1 rounded text-gray-500 italic">
                      ID: #{role.role_id}
                    </span>
                  </div>

                  <h3 className="text-lg font-black text-blue-900 mb-1 group-hover:text-blue-600 transition-colors">
                    {role.name}
                  </h3>
                  
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1 text-gray-400">
                      <Users size={14} />
                      <span>{role.users?.length || 0} Users</span>
                    </div>
                    <div className="flex items-center gap-1 text-orange-600">
                      <Lock size={14} />
                      <span>Custom Perms</span>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <span className="flex items-center gap-1 text-blue-900 group-hover:translate-x-2 transition-transform">
                      VIEW DETAILS <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {filteredRoles.length === 0 && !loading && (
              <div className="col-span-full p-20 text-center border-4 border-dashed border-gray-200 rounded-sm">
                <Lock size={48} className="mx-auto text-gray-200 mb-4" />
                <p className="text-gray-400 font-black italic">No Roles Defined Yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="bg-[#1a3353] text-white p-2 px-10 flex justify-between items-center shadow-inner">
        <div className="flex items-center gap-4 text-[9px] italic text-blue-300 tracking-widest uppercase">
          <Shield size={12} />
          Total Defined Roles: {filteredRoles.length}
        </div>
        <div className="text-[9px] text-white/30">
          Maximo Clone Security Module v1.0
        </div>
      </div>
    </div>
  );
};

export default RoleList;